import { FeatureFlag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import type { WebsiteConfig } from '@ps/web-martech-cms-types';
import { useTrackers } from '@ps/web-shared-analytics';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router';

import LoadingIndicator from '@swf/components/LoadingIndicator/LoadingIndicator';
import { useTouchpoint } from '@swf/components/Touchpoint';
import useRequest from '@swf/core/hooks/useRequest';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import IDScanProvider from '@swf/verification/IDScanProvider/IDScanProvider';
import IncodeProvider from '@swf/verification/IncodeProvider';
import SumSubProvider from '@swf/verification/SumSubProvider/SumSubProvider';

import type { OcrData, ScoreResponse } from '../IncodeProvider/Incode/incode_types';

import styles from './AccountVerification.scss';
import { JourneyState, Status, Vendor, VerificationResult } from './types';
import { useVerificationStragety } from './useVerificationStragety';
import withFeedbackButton from './withFeedbackButton';

interface Props extends React.PropsWithChildren {
  className?: string;
  isModal?: boolean;
  onClose?(): void;
  onError?(): void;
  onRestart?(): void;
  openIDScan?: boolean;
  handleCancel?(): void;
  showButton?: boolean;
  onComplete?(data?: OcrData, score?: ScoreResponse): void;
  rowLayout?: boolean;
  infoMessage?: React.ReactElement;
  titleClassName?: string;
  title?: TranslationTag;
}

const SumSubWithFeedbackButton = withFeedbackButton(SumSubProvider);
const IDScanWithFeedbackButton = withFeedbackButton(IDScanProvider);
const IncodeWithFeedbackButton = withFeedbackButton(IncodeProvider);

const AccountVerification: React.FC<Props> = (props) => {
  const {
    onComplete,
    onError,
    onRestart,
    showButton,
    isModal,
    rowLayout,
    infoMessage,
    titleClassName,
    title
  } = props;
  const verificationStrategy = useVerificationStragety();
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const strategy = isClientAuthenticated && hasClientSession ? verificationStrategy : null;
  const skip = !useFeature(FeatureFlag.FeedbackVerification);
  const touchpointVerificationConfig = useSelector((rootState: RootState) =>
    select.cms.getWebsiteConfig(rootState, 'touchpoint')
  ) as WebsiteConfig['touchpoint'] | undefined;
  const { isReady: isTouchpointReady, show: showTouchpoint } = useTouchpoint(
    skip,
    false,
    touchpointVerificationConfig
  );
  const { documentsSubmitted: documentsSubmittetState } = useSelector(select.localSettings.get);
  const [documentsSubmitted] = useState(documentsSubmittetState);
  const [journeyInProgress, setJourneyInProgress] = useState(false);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const checkRMOK = useFeature(FeatureFlag.VerificationRMOKCheck);
  const isRealMoneyOK = useSelector(select.account.isRealMoneyOK);
  const pathName = useLocation().pathname;
  const { pageView } = useTrackers();
  const isEmbedded = useSelector(select.device.isEmbedded);

  const ref = useRef({
    journeyInProgress,
    documentsSubmitted,
    vendorName: '',
    vrefId: '',
    isTouchpointReady: false,
    showTouchpoint
  });

  const request = useRequest();
  const sendVerificationState = (state: string) => {
    const { vendorName, vrefId } = ref.current;
    if (vendorName && vrefId && state) {
      request({
        service: '/api/v1-preview/account/docverify/state',
        method: 'POST',
        http: true,
        retry: { count: 2 },
        body: { vendor: vendorName, vrefId, state }
      });
    }
  };

  const sendTouchpointFeedback = useCallback(
    (triggerPoint: string) =>
      request({
        service: '/api/v1-preview/account/touchpoint/feedback',
        method: 'POST',
        http: true,
        retry: { count: 2 },
        body: { triggerPoint }
      }),
    [request]
  );

  const sendVerificationResult = (result: string) => {
    const { vendorName, vrefId } = ref.current;
    if (vendorName && vrefId && result) {
      request({
        service: '/api/v1-preview/account/docverify/result',
        method: 'POST',
        http: true,
        retry: { count: 2 },
        body: { vendor: vendorName, vrefId, result }
      });
    }
  };

  const handleJourneyComplete = (args?: {
    data?: OcrData;
    score?: ScoreResponse;
    result?: VerificationResult;
  }) => {
    const { data, score, result } = args ?? {};
    setJourneyInProgress(false);
    if (!documentsSubmitted && isTouchpointReady) {
      showTouchpoint();
      sendTouchpointFeedback(Status.FINISHED);
    }

    if (data && score) {
      let verificationResult;
      switch (score.overall.status) {
        case 'OK':
          verificationResult = VerificationResult.PASS;
          break;
        default:
          verificationResult = VerificationResult.FAIL;
          break;
      }
      sendVerificationResult(verificationResult);
      onComplete?.(data, score);
    }

    if (result) {
      sendVerificationResult(result);
    }

    sendVerificationState(JourneyState.COMPLETED);
    pageView(pathName, 'Finish Verification');
  };

  const handleJourneyInit = () => setJourneyInProgress(true);
  const handleShowFeedbackScreen = () => isTouchpointReady && showTouchpoint();

  const handleIDScanRefId = (jid: string | undefined) => {
    if (jid) {
      const { journeyInProgress, documentsSubmitted } = ref.current;
      ref.current = {
        journeyInProgress,
        documentsSubmitted,
        vendorName: Vendor.IDSCAN,
        vrefId: jid,
        isTouchpointReady,
        showTouchpoint
      };
    }
  };

  const handleSumsubRefId = (jid: string | undefined) => {
    if (jid) {
      const { journeyInProgress, documentsSubmitted } = ref.current;
      ref.current = {
        journeyInProgress,
        documentsSubmitted,
        vendorName: Vendor.SUMSUB,
        vrefId: jid,
        isTouchpointReady,
        showTouchpoint
      };
    }
  };

  const handleIncodeOnboardingId = (jid: string | undefined) => {
    if (jid) {
      const { journeyInProgress, documentsSubmitted } = ref.current;

      ref.current = {
        journeyInProgress,
        documentsSubmitted,
        vendorName: Vendor.INCODE,
        vrefId: jid,
        isTouchpointReady,
        showTouchpoint
      };
    }
  };

  useEffect(() => {
    const { vendorName, vrefId } = ref.current;
    ref.current = {
      journeyInProgress,
      documentsSubmitted,
      vendorName,
      vrefId,
      isTouchpointReady,
      showTouchpoint
    };
  }, [documentsSubmitted, journeyInProgress, isTouchpointReady, showTouchpoint]);

  useEffect(
    () => () => {
      const { journeyInProgress, documentsSubmitted, isTouchpointReady, showTouchpoint } =
        ref.current;

      if (!documentsSubmitted && journeyInProgress) {
        sendVerificationState(JourneyState.CANCELLED);
        if (isTouchpointReady) {
          showTouchpoint();
          sendTouchpointFeedback(Status.ABANDONED);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (journeyInProgress) {
      sendVerificationState(JourneyState.STARTED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyInProgress]);

  if (checkRMOK && !isRealMoneyOK) {
    return <Redirect to={{ pathname: '/account/cashier/rmf' }} />;
  } else if (isEmbedded && canUseDOM && strategy !== 'incode') {
    window.starsweb?.app?.execute('docverify');
    window.close();
    return;
  } else {
    switch (strategy) {
      case 'sumsub':
        return (
          <div className={styles.container}>
            <SumSubWithFeedbackButton
              showFeedbackButton={sdkInitialized && !!documentsSubmitted && !skip}
              onSdkInit={() => setSdkInitialized(true)}
              showFeedbackScreen={handleShowFeedbackScreen}
              onComplete={onComplete}
              showButton={showButton}
              onJourneyComplete={handleJourneyComplete}
              onJourneyInitialized={handleJourneyInit}
              onJourneyRefId={handleSumsubRefId}
              isModal={isModal}
              {...props}
            />
          </div>
        );
      case 'idscan':
        return (
          <div className={styles.container}>
            <IDScanWithFeedbackButton
              showFeedbackScreen={handleShowFeedbackScreen}
              showFeedbackButton={!!documentsSubmitted && !skip}
              onComplete={onComplete}
              {...props}
              onJourneyComplete={handleJourneyComplete}
              onJourneyInitialized={handleJourneyInit}
              onJourneyRefId={handleIDScanRefId}
              rowLayout={rowLayout}
              infoMessage={infoMessage}
              title={title}
              titleClassName={titleClassName}
            />
          </div>
        );
      case 'incode':
        return (
          <div className={styles.container}>
            <IncodeWithFeedbackButton
              showFeedbackScreen={handleShowFeedbackScreen}
              showFeedbackButton={!!documentsSubmitted && !skip}
              onError={onError}
              onJourneyRestart={onRestart}
              onJourneyInitialized={handleJourneyInit}
              onJourneyRefId={handleIncodeOnboardingId}
              onJourneyComplete={handleJourneyComplete}
            />
          </div>
        );
      default:
        return <LoadingIndicator loading={!strategy} />;
    }
  }
};

export default AccountVerification;
